import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User } from '../services/authService';
import { authService, LoginResponse } from '../services/authService';
import { activityTracker } from '../services/activityTracker';
import { setSentryUser, clearSentryUser } from '../../config/sentry';
import { closeAgentPhoneWindow } from '../../services/agentPhone/agentPhoneBridge';
import {
  AGENT_PHONE_POPUP_BLOCKED_KEY,
  AGENT_PHONE_SESSION_PRIMED_KEY,
} from '../../services/agentPhone/types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<LoginResponse>;
  verifyMfaLogin: (
    mfaToken: string,
    code: string,
    options?: { rememberMe?: boolean; trustDevice?: boolean },
  ) => Promise<void>;
  resendMfaCode: (mfaToken: string) => Promise<void>;
  logout: () => void;
  refreshUser: (forceRefresh?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  // Load user data on mount
  useEffect(() => {
    const loadUser = async () => {
      console.log('AuthContext: Starting to load user data');
      try {
        if (authService.isAuthenticated()) {
          console.log('AuthContext: User is authenticated, fetching user data');
          const userData = await authService.getCurrentUser();
          console.log('AuthContext: User data loaded successfully:', userData);
          setUser(userData);
          localStorage.setItem('userId', userData._id);
          
          // Set Sentry user context
          if (userData?._id && userData?.companyEmail) {
            setSentryUser(userData._id, userData.companyEmail, userData.username || userData.companyEmail);
          }
          
          // Start activity tracking for authenticated user
          activityTracker.restartTracking();
        } else {
          console.log('AuthContext: User is not authenticated');
        }
      } catch (err) {
        console.error('AuthContext: Error loading user:', err);
        setError('Failed to load user data');
        // Clear token locally without making API call to prevent loop
        localStorage.removeItem('token');
        authService.token = null;
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const finishLogin = useCallback((userData: User) => {
    setUser(userData);
    localStorage.setItem('userId', userData._id);
    if (userData?._id && userData?.companyEmail) {
      setSentryUser(userData._id, userData.companyEmail, userData.username || userData.companyEmail);
    }
    setTimeout(() => {
      activityTracker.restartTracking();
    }, 200);
  }, []);

  const login = useCallback(async (username: string, password: string, rememberMe?: boolean) => {
    console.log('AuthContext: Starting login process');
    try {
      setError(null);
      setLoading(true);
      const result = await authService.login({ username, password, rememberMe });
      if ('user' in result) {
        finishLogin(result.user);
      }
      return result;
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      setError('Invalid username or password');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [finishLogin]);

  const verifyMfaLogin = useCallback(async (
    mfaToken: string,
    code: string,
    options?: { rememberMe?: boolean; trustDevice?: boolean },
  ) => {
    try {
      setError(null);
      setLoading(true);
      const { user: userData } = await authService.verifyMfa({
        mfaToken,
        code,
        rememberMe: options?.rememberMe,
        trustDevice: options?.trustDevice,
      });
      finishLogin(userData);
    } catch (err) {
      console.error('AuthContext: MFA verification error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [finishLogin]);

  const resendMfaCode = useCallback(async (mfaToken: string) => {
    await authService.resendMfa(mfaToken);
  }, []);

  const logout = useCallback(() => {
    console.log('AuthContext: Logging out');
    closeAgentPhoneWindow();
    try {
      sessionStorage.removeItem(AGENT_PHONE_SESSION_PRIMED_KEY);
      sessionStorage.removeItem(AGENT_PHONE_POPUP_BLOCKED_KEY);
    } catch {
      /* ignore */
    }
    authService.logout();
    setUser(null);
    localStorage.removeItem('userId');
    
    // Clear Sentry user context
    clearSentryUser();
    
    // Stop activity tracking on logout
    activityTracker.stopTracking();
  }, []);

  const refreshUser = useCallback(async (forceRefresh: boolean = true) => {
    try {
      setError(null);
      if (!userRef.current) {
        setLoading(true);
      }
      const userData = await authService.getCurrentUser(forceRefresh);
      setUser({ ...userData });
      localStorage.setItem('userId', userData._id);

      if (userData?._id && userData?.companyEmail) {
        setSentryUser(userData._id, userData.companyEmail, userData.username || userData.companyEmail);
      }
    } catch (err) {
      console.error('AuthContext: Error refreshing user:', err);
      setError('Failed to refresh user data');
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      verifyMfaLogin,
      resendMfaCode,
      logout,
      refreshUser,
    }),
    [user, loading, error, login, verifyMfaLogin, resendMfaCode, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 